import { BookOpenCheck, CheckCircle2, ClipboardList, Sparkles, Target } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import StudentBackButton from '../components/StudentBackButton.jsx'
import { recordStudentGrowthEvent } from '../api.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { STUDENT_COMPETITIONS, STUDENT_COMPETITION_FILTERS, competitionMatchesFilter, competitionScore } from '../student-competitions.js'

const STUDENT_PROFILE_KEY = 'student_profile'
const COMPETITION_PLAN_KEY = 'student_plan_competitions'

const MAJOR_LABELS = {
  computer: '计算机 / 软件 / 人工智能',
  ecommerce: '电商 / 市场 / 新媒体',
  design: '设计 / 视觉 / 数字媒体',
  business: '经管 / 财会 / 金融',
  language: '外语 / 翻译 / 文科',
  education: '教育 / 师范',
  engineering: '电子 / 机械 / 自动化',
  medical: '医学 / 护理 / 健康管理',
  general: '通用方向',
}

const GRADE_LABELS = {
  freshman: '大一',
  sophomore: '大二',
  junior: '大三',
  senior: '大四',
  graduate: '毕业 / 求职',
  zero: '零基础 / 转专业',
}

function zh(value) {
  if (!value) return ''
  if (Array.isArray(value.zh)) return value.zh
  if (typeof value.zh === 'string') return value.zh
  if (Array.isArray(value)) return value[0] || ''
  return String(value)
}

function zhList(value) {
  const result = zh(value)
  return Array.isArray(result) ? result : result ? [result] : []
}

function readJson(key, fallback) {
  if (typeof window === 'undefined') return fallback
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || 'null')
    return parsed || fallback
  } catch {
    return fallback
  }
}

function savePlan(items) {
  if (typeof window !== 'undefined') window.localStorage.setItem(COMPETITION_PLAN_KEY, JSON.stringify(items))
}

function resumeValue(item) {
  if (item.level === 'S') return '非常高'
  if (item.level === 'A') return '较高'
  if (item.level === 'B') return '中等'
  return '基础补充'
}

function todayTask(item) {
  const title = zh(item.title)
  const firstStep = zhList(item.whatToPrepare)[0] || '整理报名规则和作品要求'
  return `今天先确认「${title}」的报名时间和规则，再完成一个小步骤：${firstStep}。`
}

function CompetitionCard({ added, expanded, item, onToggleExpand, onTogglePlan }) {
  const title = zh(item.title)
  const tags = zhList(item.tags)
  const prepare = zhList(item.whatToPrepare)
  const projects = zhList(item.goodProjectIdeas)

  return (
    <article className={`student-competition-card competition-level-${item.level}`}>
      <div className="student-project-card-head">
        <div>
          <span>{zh(item.category)}</span>
          <h3>{title}</h3>
          {zh(item.alias) ? <p className="student-competition-alias">{zh(item.alias)}</p> : null}
        </div>
        <strong className="student-competition-level">{item.level}</strong>
      </div>

      <div className="student-competition-badges">
        {tags.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}
      </div>

      <dl className="student-project-meta competition-meta-grid">
        <div><dt>适合年级</dt><dd>{(item.suitableGrades || []).map((key) => GRADE_LABELS[key] || key).slice(0, 3).join(' / ') || '不限'}</dd></div>
        <div><dt>适合专业</dt><dd>{(item.majorGroups || []).map((key) => MAJOR_LABELS[key] || key).slice(0, 3).join(' / ') || '通用'}</dd></div>
        <div><dt>难度</dt><dd>{zh(item.difficulty) || '中等'}</dd></div>
        <div><dt>简历价值</dt><dd>{resumeValue(item)}</dd></div>
        <div><dt>推荐等级</dt><dd>{item.level} 级</dd></div>
        <div><dt>周期</dt><dd>{zh(item.cycle) || '1-3 个月'}</dd></div>
      </dl>

      <div className="student-competition-fit">
        <strong>推荐理由</strong>
        <p>{zh(item.value) || '适合沉淀项目经历、作品集和简历表达。'}</p>
      </div>

      {expanded ? (
        <div className="student-competition-expanded">
          <div>
            <strong>准备清单</strong>
            <ul>{(prepare.length ? prepare : ['阅读赛事规则', '确认作品要求', '拆解 2 周准备计划']).map((task) => <li key={task}>{task}</li>)}</ul>
          </div>
          <div>
            <strong>适配项目</strong>
            <ul>{(projects.length ? projects : ['课程项目升级', '数据看板', 'AI 小工具']).map((project) => <li key={project}>{project}</li>)}</ul>
          </div>
          <div className="student-competition-ai-advice">
            <strong><Sparkles size={15} /> 学习资源</strong>
            <p>把赛事规则整理成任务清单，每周检查作品、材料和答辩准备是否到位。</p>
          </div>
        </div>
      ) : null}

      <div className="student-project-actions">
        <button type="button" onClick={() => onToggleExpand(item.id)}>
          <ClipboardList size={15} /> {expanded ? '收起成长计划' : '查看成长计划'}
        </button>
        <button type="button" onClick={() => onTogglePlan(item)}>
          <CheckCircle2 size={15} /> {added ? '已加入成长计划' : '加入成长计划'}
        </button>
      </div>
    </article>
  )
}

function StudentCompetitionsPage() {
  const { language } = useLanguage()
  const navigate = useNavigate()
  const [profile] = useState(() => readJson(STUDENT_PROFILE_KEY, null))
  const [filter, setFilter] = useState('all')
  const [expandedId, setExpandedId] = useState('')
  const [plan, setPlan] = useState(() => readJson(COMPETITION_PLAN_KEY, []))

  const competitions = useMemo(() => (
    STUDENT_COMPETITIONS
      .filter((item) => competitionMatchesFilter(item, filter))
      .sort((left, right) => competitionScore(right, profile) - competitionScore(left, profile))
  ), [filter, profile])

  const togglePlan = (item) => {
    const exists = plan.some((entry) => entry.id === item.id)
    const next = exists
      ? plan.filter((entry) => entry.id !== item.id)
      : [...plan, { id: item.id, title: zh(item.title), task: todayTask(item), addedAt: new Date().toISOString() }]
    setPlan(next)
    savePlan(next)
    if (!exists) {
      recordStudentGrowthEvent({
        eventType: 'competition_added',
        title: `加入竞赛规划：${zh(item.title)}`,
        description: todayTask(item),
        metadata: { competitionId: item.id, level: item.level },
      }).catch(() => {})
    }
  }

  if (language === 'en') {
    // English mode currently shares the same competition catalog content.
  }

  return (
    <div className="student-page student-library-page page-container">
      <StudentBackButton />
      <section className="student-hero">
        <span>学生专区</span>
        <h1>竞赛成长计划</h1>
        <p>根据专业、年级和目标推荐适合的竞赛，并把推荐理由、准备周期、简历价值和学习资源拆清楚。</p>
      </section>

      <section className="student-library-profile">
        <div>
          <span>当前推荐依据</span>
          <strong>{profile ? [MAJOR_LABELS[profile.majorGroup], GRADE_LABELS[profile.grade]].filter(Boolean).join(' / ') : '通用推荐 / 暂未选择画像'}</strong>
        </div>
        <button type="button" className="secondary-button" onClick={() => navigate('/growth')}>修改选择</button>
      </section>

      <div className="student-competition-filters" aria-label="比赛筛选">
        {STUDENT_COMPETITION_FILTERS.map((item) => (
          <button aria-pressed={filter === item.key} className={filter === item.key ? 'is-active' : ''} key={item.key} type="button" onClick={() => setFilter(item.key)}>
            {item.zh || item.key}
          </button>
        ))}
      </div>

      {!competitions.length ? (
        <section className="student-roadmap-empty">
          <span><Target size={18} /> 暂无竞赛</span>
          <h1>当前筛选下没有匹配结果</h1>
          <p>可以切换筛选条件，或先回到成长中心选择学习方向。</p>
          <Link className="primary-button" to="/growth">返回成长中心</Link>
        </section>
      ) : null}

      <section className="student-competition-grid">
        {competitions.map((item) => (
          <CompetitionCard
            added={plan.some((entry) => entry.id === item.id)}
            expanded={expandedId === item.id}
            item={item}
            key={item.id}
            onToggleExpand={(id) => setExpandedId((current) => (current === id ? '' : id))}
            onTogglePlan={togglePlan}
          />
        ))}
      </section>

      <section className="student-library-profile">
        <div>
          <span>下一步</span>
          <strong>把竞赛目标转成项目成长计划，再进入简历优化。</strong>
        </div>
        <Link className="secondary-button" to="/projects/recommendations"><BookOpenCheck size={15} /> 项目成长计划</Link>
      </section>
    </div>
  )
}

export default StudentCompetitionsPage
